import React, { useState, useEffect } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { yuzTani, livenessSessionOlustur } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { AWS_REGION } from '../config/amplifyConfig';
import {
  HiCamera,
  HiShieldCheck,
  HiCheckCircle,
  HiRefresh,
  HiUserCircle,
  HiExclamationCircle,
  HiSparkles
} from 'react-icons/hi';
import { MdFaceRetouchingNatural } from 'react-icons/md';
import toast from 'react-hot-toast';
import '@aws-amplify/ui-react/styles.css';
import './YuzTaramaPage.css';

const YuzTaramaPage = () => {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [livenessStep, setLivenessStep] = useState('idle'); // idle, liveness, recognizing, done
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fotosensitivite uyarısını ve gereksiz elementleri DOM'dan kaldır
  useEffect(() => {
    if (livenessStep === 'liveness') {
      const hidePhotosensitivity = setInterval(() => {
        // Tüm alert rolündeki elementleri gizle
        const alerts = document.querySelectorAll('[data-amplify-liveness-detector] [role="alert"]');
        alerts.forEach(el => {
          el.style.display = 'none';
          el.style.height = '0';
          el.style.overflow = 'hidden';
          el.style.visibility = 'hidden';
          el.style.position = 'absolute';
          el.style.left = '-9999px';
        });

        // Fotosensitivity içeren tüm elementleri gizle
        const photosensitivity = document.querySelectorAll('[data-amplify-liveness-detector] *');
        photosensitivity.forEach(el => {
          const text = el.textContent || '';
          if (text.includes('Fotosensitivite') || text.includes('photosensitiv') ||
              text.includes('Instruction:') || text.includes('Before starting')) {
            el.style.display = 'none';
            el.style.height = '0';
            el.style.overflow = 'hidden';
            el.style.visibility = 'hidden';
          }
        });

        // Tüm h1, h2, h3, h4 başlıklarını gizle (hint'ler hariç)
        const headers = document.querySelectorAll('[data-amplify-liveness-detector] h1, [data-amplify-liveness-detector] h2, [data-amplify-liveness-detector] h3, [data-amplify-liveness-detector] h4');
        headers.forEach(el => {
          const parent = el.closest('[class*="hint"]') || el.closest('[class*="Hint"]');
          if (!parent) {
            el.style.display = 'none';
            el.style.height = '0';
          }
        });

        // İlk div'i gizle (genelde fotosensitivite uyarısı burada)
        const firstDiv = document.querySelector('[data-amplify-liveness-detector] > div:first-child');
        if (firstDiv) {
          firstDiv.style.display = 'none';
          firstDiv.style.height = '0';
          firstDiv.style.overflow = 'hidden';
        }
      }, 100); // Her 100ms'de bir kontrol et

      return () => clearInterval(hidePhotosensitivity);
    }
  }, [livenessStep]);

  // Liveness session oluştur
  const handleStartLiveness = async () => {
    try {
      setIsCreatingSession(true);
      setError(null);
      setResult(null);

      console.log('🚀 Liveness session oluşturuluyor...', { okulId: user?.okulId });

      // Backend'den liveness session ID al
      const response = await livenessSessionOlustur(user?.okulId);

      console.log('📥 Backend response:', response);

      if (response.success && response.sessionId) {
        console.log('✅ Session ID alındı:', response.sessionId);
        console.log('🌍 AWS Region:', AWS_REGION);
        setSessionId(response.sessionId);
        setLivenessStep('liveness');
        toast.success('Canlılık testi başlatıldı!');
      } else {
        console.error('❌ Session oluşturulamadı:', response);
        setError(response.mesaj || 'Liveness session oluşturulamadı');
        toast.error(response.mesaj || 'Liveness session oluşturulamadı');
      }
    } catch (err) {
      console.error('❌ Exception:', err);
      setError(err.message || 'Bir hata oluştu');
      toast.error(err.message || 'Bir hata oluştu');
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Liveness testi başarılı olduğunda
  const handleLivenessComplete = async () => {
    console.log('🎉 Liveness testi başarılı!');
    toast.success('Canlılık testi geçildi!');
    setLivenessStep('recognizing');
    setLoading(true);

    try {
      // ÖNEMLI: Liveness session'dan ReferenceImage gelmediği için
      // kullanıcıdan bir fotoğraf çekmemiz gerekiyor
      // Basit çözüm: Video stream'den snapshot al

      let photoBase64 = '';

      // Video elementini bul ve snapshot çek
      try {
        const videoElement = document.querySelector('video');
        if (videoElement && videoElement.srcObject) {
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(videoElement, 0, 0);
          photoBase64 = canvas.toDataURL('image/jpeg').split(',')[1];
          console.log('📸 Kameradan fotoğraf çekildi');
        } else {
          console.warn('⚠️ Video stream bulunamadı, session ID ile devam ediliyor');
        }
      } catch (err) {
        console.warn('⚠️ Fotoğraf çekme hatası:', err);
      }

      // Yüz tanıma yap (sessionId ile!)
      const response = await yuzTani(
        photoBase64,  // ← Gerçek fotoğraf
        user?.okulId,
        user?.collectionId,
        sessionId  // ← ZORUNLU! Liveness session ID
      );

      if (response.success) {
        setResult({
          cocukAdi: response.cocukAdi,
          veliAdi: response.veliAdi,
          ogretmenEmail: response.ogretmenEmail,
          benzerlik: response.benzerlik,
          liveness: response.liveness,
          mesaj: response.mesaj
        });
        setLivenessStep('done');
        toast.success(`Yüz tanındı: ${response.veliAdi}`);
      } else {
        setError(response.mesaj || 'Yüz tanınamadı');
        setLivenessStep('idle');
        toast.error(response.mesaj || 'Yüz tanınamadı');
      }
    } catch (err) {
      setError(err.message || 'Bir hata oluştu');
      setLivenessStep('idle');
      toast.error(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
      setSessionId(null);
    }
  };

  // Liveness testi hata verdiğinde
  const handleLivenessError = (error) => {
    console.error('❌ Liveness hatası:', error);
    console.error('❌ Error details:', JSON.stringify(error, null, 2));
    console.error('❌ Error message:', error?.message);
    console.error('❌ Error name:', error?.name);
    setError(`Canlılık testi başarısız! ${error?.message || 'Lütfen tekrar deneyin.'}`);
    setLivenessStep('idle');
    setSessionId(null);
    toast.error(`Canlılık testi başarısız! ${error?.message || ''}`);
  };

  // Tekrar dene
  const handleRetry = () => {
    setError(null);
    setResult(null);
    setSessionId(null);
    setLivenessStep('idle');
  };

  return (
    <div className="yuz-tarama-container">
      {/* Main Card */}
      <div className="camera-card">
        {/* Compact Header - Sadece idle state'te göster */}
        {livenessStep === 'idle' && (
          <div className="card-header-compact">
            <h2 className="card-title-compact">
              <HiShieldCheck className="title-icon" />
              Güvenli Yüz Tanıma
            </h2>
          </div>
        )}

        <div className="card-body">
          {/* IDLE: Başlangıç ekranı */}
          {livenessStep === 'idle' && (
            <div className="liveness-idle">
              <div className="camera-placeholder-compact">
                <div className="placeholder-icon-compact">
                  <HiCamera />
                </div>
                <h3>Canlılık Testi ile Güvenli Tanıma</h3>
                <div className="feature-badges">
                  <span className="feature-badge">
                    <HiShieldCheck /> Güvenli
                  </span>
                  <span className="feature-badge">
                    <HiSparkles /> AI
                  </span>
                  <span className="feature-badge">
                    <MdFaceRetouchingNatural /> Canlılık
                  </span>
                </div>
              </div>

              {/* Kompakt Talimatlar - Üstte */}
              <div className="quick-instructions">
                <div className="quick-step">
                  <span className="step-num">1</span>
                  <span>Başlat'a tıkla</span>
                </div>
                <div className="quick-step">
                  <span className="step-num">2</span>
                  <span>Kamera izni ver</span>
                </div>
                <div className="quick-step">
                  <span className="step-num">3</span>
                  <span>Talimatları takip et</span>
                </div>
              </div>

              <div className="controls">
                <button
                  className="btn btn-primary btn-large"
                  onClick={handleStartLiveness}
                  disabled={isCreatingSession}
                >
                  {isCreatingSession ? (
                    <>
                      <div className="spinner-sm"></div>
                      <span>Hazırlanıyor...</span>
                    </>
                  ) : (
                    <>
                      <HiCamera />
                      <span>Başlat</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* LIVENESS: Canlılık testi UI */}
          {livenessStep === 'liveness' && sessionId && (
            <div className="liveness-detector-wrapper">
              <FaceLivenessDetector
                sessionId={sessionId}
                region={AWS_REGION}
                onAnalysisComplete={handleLivenessComplete}
                onError={handleLivenessError}
                displayText={{
                  photosensitivyWarningHeadingText: 'Fotosensitivite Uyarısı',
                  photosensitivityWarningBodyText: 'Bu kontrol renkli ışıklar içerir.',
                  photosensitivityWarningInfoText: 'Işığa duyarlıysanız, lütfen dikkatli olun.',
                  goodFitCaptionText: 'İyi konum',
                  tooFarCaptionText: 'Çok uzak',
                  startScreenBeginCheckText: 'Başlat',
                  hintCenterFaceText: 'Yüzünüzü ortaya alın',
                  hintTooManyFacesText: 'Çok fazla yüz var',
                  hintFaceDetectedText: 'Yüz tespit edildi',
                  hintCanNotIdentifyText: 'Yüz tanınamadı',
                  hintTooCloseText: 'Çok yakın',
                  hintTooFarText: 'Çok uzak',
                  hintConnectingText: 'Bağlanıyor...',
                  hintVerifyingText: 'Doğrulanıyor...',
                  hintIlluminationTooBrightText: 'Çok parlak',
                  hintIlluminationTooDarkText: 'Çok karanlık',
                  hintIlluminationNormalText: 'İyi aydınlatma',
                  hintHoldFaceForFreshnessText: 'Yüzünüzü sabit tutun',
                  hintMoveFaceFrontOfCameraText: 'Yüzünüzü kameraya doğru tutun',
                  cameraMinSpecificationsHeadingText: 'Kamera gereksinimleri karşılanmıyor',
                  cameraMinSpecificationsMessageText: 'Lütfen daha iyi bir kamera kullanın',
                  cameraNotFoundHeadingText: 'Kamera bulunamadı',
                  cameraNotFoundMessageText: 'Lütfen kameranın bağlı olduğundan emin olun',
                  a11yVideoLabelText: 'Canlılık testi video akışı',
                  cancelLivenessCheckText: 'İptal',
                  recordingIndicatorText: 'Kayıt',
                }}
              />
            </div>
          )}

          {/* RECOGNIZING: Yüz tanıma yapılıyor */}
          {livenessStep === 'recognizing' && (
            <div className="recognizing-state">
              <div className="recognizing-content">
                <div className="spinner-large"></div>
                <div className="recognizing-steps">
                  <div className="step completed">
                    <HiCheckCircle />
                    <span>Canlılık testi geçildi</span>
                  </div>
                  <div className="step active">
                    <div className="spinner-sm"></div>
                    <span>Yüz tanıma yapılıyor...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DONE: Başarılı sonuç göster */}
          {livenessStep === 'done' && result && (
            <div className="result-card success">
              <div className="result-header">
                <div className="success-icon">
                  <HiCheckCircle />
                </div>
                <h3>Yüz Tanındı!</h3>
              </div>
              <div className="result-body">
                <div className="result-item">
                  <span className="label">Çocuk:</span>
                  <span className="value">{result.cocukAdi}</span>
                </div>
                <div className="result-item">
                  <span className="label">Veli:</span>
                  <span className="value">{result.veliAdi}</span>
                </div>
                <div className="result-item">
                  <span className="label">Benzerlik:</span>
                  <span className="value badge-success">%{result.benzerlik}</span>
                </div>
                {result.liveness && (
                  <div className="result-item">
                    <span className="label">Canlılık Güveni:</span>
                    <span className="value badge-success">%{result.liveness.confidence}</span>
                  </div>
                )}
                <div className="result-item">
                  <span className="label">Durum:</span>
                  <span className="value">{result.mesaj}</span>
                </div>
                <div className="email-notification">
                  <HiUserCircle />
                  <p>Öğretmen / Yetkili bilgilendirildi: <strong>{result.ogretmenEmail}</strong></p>
                </div>
              </div>
              <div className="result-actions">
                <button className="btn btn-primary" onClick={handleRetry}>
                  <HiRefresh />
                  <span>Yeni Tanıma</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hata Gösterimi */}
      {error && (
        <div className="result-card error">
          <div className="result-header">
            <div className="error-icon">
              <HiExclamationCircle />
            </div>
            <h3>Hata Oluştu</h3>
          </div>
          <div className="result-body">
            <p className="error-message">{error}</p>
          </div>
          <div className="result-actions">
            <button className="btn btn-primary" onClick={handleRetry}>
              <HiRefresh />
              <span>Tekrar Dene</span>
            </button>
          </div>
        </div>
      )}

      {/* Kullanım Talimatları - Sadece idle durumunda göster */}
      {livenessStep === 'idle' && (
        <div className="instructions-card-compact">
          <div className="security-features-compact">
            <h4>
              <HiSparkles />
              Güvenlik Özellikleri
            </h4>
            <div className="features-grid-compact">
              <div className="feature-item">
                <HiCheckCircle />
                <span>Fotoğrafla tanıma yapılamaz</span>
              </div>
              <div className="feature-item">
                <HiCheckCircle />
                <span>Yapay zeka ile gerçek kişi kontrolü</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YuzTaramaPage;
